import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TextField } from '@/ui/form/controls/TextField'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { editCompany, getCompanyConfigDetails } from '@/lib/api/company/editCompany'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { toast } from '@/lib/toasts/api'
import { useMutateGusData } from '@/modules/settings/react-query/basic/useMutateGusData'

interface EditableFields {
  companyName: string
  invoiceCompanyName: string
  nip: string
  regon: string
  krs: string
  email: string
  notificationsEmail: string
  contactEmail: string
  contactPhone: string
  licenseNumber: string
  street: string
  houseNumber: string
  buildingNumber: string
  postalCode: string
  city: string
  uniqueIdentifier: string
}

const emptyFields: EditableFields = {
  companyName: '',
  invoiceCompanyName: '',
  nip: '',
  regon: '',
  krs: '',
  email: '',
  notificationsEmail: '',
  contactEmail: '',
  contactPhone: '',
  licenseNumber: '',
  street: '',
  houseNumber: '',
  buildingNumber: '',
  postalCode: '',
  city: '',
  uniqueIdentifier: '',
}

type FieldErrors = Partial<Record<keyof EditableFields, string>>

const EMAIL_FIELDS = ['email', 'notificationsEmail', 'contactEmail'] as const

// Kilka adresów w jednym polu (przecinek/średnik) jest dopuszczalne, np. do powiadomień.
const isValidEmailList = (value: string) =>
  value
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .every((part) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(part))

// Ta sama suma kontrolna co ValidNipRule na serwerze.
const isValidNip = (value: string) => {
  if (!/^\d{10}$/.test(value)) return false
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  const sum = weights.reduce((acc, w, i) => acc + w * Number(value[i]), 0)
  return sum % 11 !== 10 && sum % 11 === Number(value[9])
}

const SERVER_FIELD_ERRORS: Record<string, [keyof EditableFields, string]> = {
  'NIP.INVALID': ['nip', 'administrationPanel-companies.validation-nip-invalid'],
  'KRS.INVALID': ['krs', 'administrationPanel-companies.validation-krs-invalid'],
}

type Props = {
  editedCompanyId: string
  onSuccess: () => void
  onCancel: () => void
}

export const EditCompanyForm: React.FC<Props> = ({ editedCompanyId, onSuccess, onCancel }) => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const [fields, setFields] = React.useState<EditableFields>(emptyFields)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({})
  const gus = useMutateGusData(editedCompanyId)
  const gusRequestId = React.useRef(0)
  const [fetchingGus, setFetchingGus] = React.useState(false)

  React.useEffect(() => {
    setFetchingGus(false)
    return () => {
      // Ignore responses after closing the form or switching companies.
      gusRequestId.current += 1
    }
  }, [editedCompanyId])

  // Fetch full company details so we can preserve untouched fields when submitting
  const { data: details, isLoading } = useQuery({
    queryKey: ['company-config-details', editedCompanyId],
    queryFn: async () => {
      const res = await getCompanyConfigDetails(editedCompanyId)
      return res.data as Record<string, any>
    },
    staleTime: 0,
  })

  React.useEffect(() => {
    if (!details) return
    setFields({
      companyName: details.companyName ?? '',
      invoiceCompanyName: details.invoiceCompanyName ?? '',
      nip: details.nip ?? '',
      regon: details.regon ?? '',
      krs: details.krs ?? '',
      email: details.email ?? '',
      notificationsEmail: details.notificationsEmail ?? '',
      contactEmail: details.contactEmail ?? '',
      contactPhone: details.contactPhone ?? '',
      licenseNumber: details.licenseNumber ?? '',
      street: details.street ?? '',
      houseNumber: details.houseNumber ?? '',
      buildingNumber: details.buildingNumber ?? '',
      postalCode: details.postalCode ?? '',
      city: details.city ?? '',
      uniqueIdentifier: details.uniqueIdentifier ?? '',
    })
  }, [details])

  const update = <K extends keyof EditableFields>(key: K, value: EditableFields[K]) => {
    setFields((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
    setError(null)
  }

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {}
    if (fields.nip && !isValidNip(fields.nip.replace(/[\s-]/g, ''))) {
      errors.nip = t('administrationPanel-companies.validation-nip-invalid')
    }
    EMAIL_FIELDS.forEach((key) => {
      if (fields[key] && !isValidEmailList(fields[key])) {
        errors[key] = t('administrationPanel-companies.validation-email-invalid')
      }
    })
    return errors
  }

  const fetchGus = async (nip: string) => {
    const requestId = ++gusRequestId.current
    const cleanNip = nip.replace(/\D/g, '')
    setFetchingGus(cleanNip.length === 10)
    if (cleanNip.length !== 10) return

    try {
      const data = await gus.mutateAsync({ nip: cleanNip })
      if (requestId !== gusRequestId.current) return
      setFields((prev) => ({
        ...prev,
        companyName: data.name || prev.companyName,
        invoiceCompanyName: prev.invoiceCompanyName || data.name || '',
        nip: data.nip || prev.nip,
        regon: data.regon || prev.regon,
        krs: data.krs || prev.krs,
        email: data.email || prev.email,
        street: data.street || prev.street,
        // HouseNumber to numer lokalu, BuildingNumber numer budynku (faktury, panel admina).
        buildingNumber: data.buildingNumber || prev.buildingNumber,
        houseNumber: data.flatNumber || prev.houseNumber,
        postalCode: data.postalCode || prev.postalCode,
        city: data.city || prev.city,
        contactPhone: data.officePhone || prev.contactPhone,
      }))
    } catch {
      // The shared mutation displays the error. Keep the editable data intact.
    } finally {
      if (requestId === gusRequestId.current) setFetchingGus(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!details || fetchingGus || submitting) return
    const clientErrors = validate()
    setFieldErrors(clientErrors)
    if (Object.keys(clientErrors).length > 0) return
    setSubmitting(true)
    setError(null)
    try {
      // Merge full backend details with form-edited fields so untouched fields
      // (subscription/payment/package etc.) are preserved exactly as-is.
      // Backend EditCompanyCommand expects Groups as long[], but the GET response
      // returns groups as [{id, name}, ...] — flatten to id list.
      const groupIds: number[] = Array.isArray(details.groups)
        ? details.groups
            .map((g: any) => (typeof g === 'object' && g !== null ? g.id : g))
            .filter((v: any) => v != null)
            .map((v: any) => Number(v))
        : Array.isArray(details.groupList)
          ? details.groupList.map((v: any) => Number(v)).filter((v: number) => !Number.isNaN(v))
          : []

      const { isEwiSystem: _stripIsEwi, ...detailsWithoutEwi } = details
      const payload = {
        ...detailsWithoutEwi,
        id: editedCompanyId,
        companyName: fields.companyName,
        invoiceCompanyName: fields.invoiceCompanyName || null,
        nip: fields.nip,
        regon: fields.regon,
        krs: fields.krs,
        email: fields.email,
        notificationsEmail: fields.notificationsEmail,
        contactEmail: fields.contactEmail,
        contactPhone: fields.contactPhone,
        licenseNumber: fields.licenseNumber,
        street: fields.street,
        houseNumber: fields.houseNumber,
        buildingNumber: fields.buildingNumber,
        postalCode: fields.postalCode,
        city: fields.city,
        groups: groupIds,
        // Don't send userRoles — when null, the handler skips role updates and
        // returns after applying the basic-field changes.
        userRoles: null,
      }
      await editCompany(payload)
      await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
      await queryClient.invalidateQueries({ queryKey: ['company-config-details', editedCompanyId] })
      toast.success({ text: t('addCompany.companyUpdatedSuccess') })
      onSuccess()
    } catch (err: any) {
      // Walidator serwera zwraca listę { code, path }; znane kody trafiają pod pole.
      const data = err?.response?.data
      const serverErrors: FieldErrors = {}
      if (Array.isArray(data)) {
        data.forEach((e: any) => {
          const known = SERVER_FIELD_ERRORS[e?.code]
          if (known) serverErrors[known[0]] = t(known[1])
        })
      }
      setFieldErrors(serverErrors)
      const msg =
        Object.values(serverErrors)[0] ||
        (typeof data?.error === 'string' ? data.error : null) ||
        t('addCompany.update-error')
      setError(msg)
      toast.error({ text: msg })
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading || !details) {
    return (
      <div className="tw-grid tw-place-items-center tw-py-12">
        <LoaderIcon className="tw-w-12 tw-animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-6">
      {error && (
        <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-800">
          {error}
        </div>
      )}

      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <TextField
          label={t('addCompany.companyName')}
          required
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.companyName}
          onChange={(e) => update('companyName', e.target.value)}
        />
        <TextField
          label={t('addCompany.invoiceCompanyName')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.invoiceCompanyName}
          onChange={(e) => update('invoiceCompanyName', e.target.value)}
        />
        <TextField
          label={t('addCompany.uniqueIdentifier')}
          value={fields.uniqueIdentifier}
          disabled
          onChange={() => {}}
        />
        <TextField
          label="NIP"
          error={fieldErrors.nip}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.nip}
          disabled={submitting}
          onChange={(e) => {
            update('nip', e.target.value)
            void fetchGus(e.target.value)
          }}
          helper={
            <button
              type="button"
              disabled={submitting || fetchingGus || fields.nip.replace(/\D/g, '').length !== 10}
              onClick={() => void fetchGus(fields.nip)}
              className="tw-text-primary hover:tw-underline disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              {fetchingGus ? t('addCompany.fetchingGusData') : t('configuration-basic.fetch-gus-data')}
            </button>
          }
        />
        <TextField
          label="REGON"
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.regon}
          onChange={(e) => update('regon', e.target.value)}
        />
        <TextField
          label="KRS"
          error={fieldErrors.krs}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.krs}
          onChange={(e) => update('krs', e.target.value)}
        />
      </div>

      <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
      <h4 className="tw-text-md tw-font-semibold">{t('addCompany.contactInfo')}</h4>
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <TextField
          label="Email"
          error={fieldErrors.email}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.email}
          onChange={(e) => update('email', e.target.value)}
        />
        <TextField
          label={t('addCompany.notificationsEmail')}
          error={fieldErrors.notificationsEmail}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.notificationsEmail}
          onChange={(e) => update('notificationsEmail', e.target.value)}
        />
        <TextField
          label={t('addCompany.contactEmail')}
          error={fieldErrors.contactEmail}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.contactEmail}
          onChange={(e) => update('contactEmail', e.target.value)}
        />
        <TextField
          label={t('addCompany.contactPhone')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.contactPhone}
          onChange={(e) => update('contactPhone', e.target.value)}
        />
      </div>

      <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
      <h4 className="tw-text-md tw-font-semibold">{t('common.address')}</h4>
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <TextField
          label={t('addCompany.street')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.street}
          onChange={(e) => update('street', e.target.value)}
        />
        <TextField
          label={t('addCompany.houseNumber')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.buildingNumber}
          onChange={(e) => update('buildingNumber', e.target.value)}
        />
        <TextField
          label={t('addCompany.flatNumber')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.houseNumber}
          onChange={(e) => update('houseNumber', e.target.value)}
        />
        <TextField
          label={t('addCompany.postalCode')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.postalCode}
          onChange={(e) => update('postalCode', e.target.value)}
        />
        <TextField
          label={t('common.city')}
          placeholder={t('addCompany.typePlaceholder')}
          value={fields.city}
          onChange={(e) => update('city', e.target.value)}
        />
      </div>

      <div className="tw-flex tw-justify-end tw-gap-4 tw-pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={submitting || fetchingGus || !fields.companyName}
          className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        >
          {submitting ? t('addCompany.saving') : t('common.save-changes')}
        </button>
      </div>
    </form>
  )
}
